import getClientTimeouts from '../selectors/getClientTimeouts';
import { ValidationResults, Input, HttpAction } from 'adaptivecards';
import {
    CardDetails,
    ExecuteActionResponse,
    MessageContext,
    TelemetryDetails,
    ElementDetails,
} from 'owa-actionable-message-v2';
import { logQoSFailure } from 'owa-analytics';
import { CustomData } from 'owa-analytics-actions';
import { getClientAuthToken, getConnectorsLTIToken } from 'owa-connectors';
import { HttpReadyState, HttpStatusCode } from 'owa-http-status-codes';
import loc, { format } from 'owa-localize';
import { actionGenericError } from 'owa-locstrings/lib/strings/actiongenericerror.locstring.json';
import { actionGenericSuccess } from 'owa-locstrings/lib/strings/actiongenericsuccess.locstring.json';
import { getOwaCanaryCookie } from 'owa-service/lib/canary';
import { getCorrelationVector } from 'owa-service/lib/correlationVector';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { trace } from 'owa-trace';
import {
    CLIENT_DEFAULT_HTTP_ACTION_TIMEOUT,
    CLIENT_DEFAULT_AUTO_INVOKE_ACTION_TIMEOUT,
    INLINE_ADAPTIVE_CARD_TYPE,
    ACTION_CARD_POST_ACTION_CLICKED,
    SINGLE_CLICK_POST_ACTION_CLICKED,
    AUTO_INVOKE_ACTION_CLICKED,
    DEFAULT_ACTION_ID,
} from '../constants';
import { getTelemetryData } from 'owa-actionable-message-analytics';
import { getActionEndpointUrlFromStore } from '../selectors/getActionEndpoint';

// Type of HttpPost Action.
const HTTP_POST_ACTION_TYPE = 'HttpPOST';

const USER_ID = 'userid';

// The request format for executing http actions from message cards
const MESSAGE_CARD_ACTION_REQUEST_FORMAT = '{0}/{1}/messages/{2}/executeAction?culture={3}';

// The request format for executing http actions from adpative cards
const ADAPTIVE_CARD_ACTION_REQUEST_FORMAT =
    '{0}/{1}/messages/{2}/adaptiveCard/executeAction?culture={3}';

// The request format for executing http actions from message cards in groups
const MESSAGE_CARD_GROUP_ACTION_REQUEST_FORMAT =
    '{0}/{1}/threads/{2}/posts/{3}/executeAction?culture={4}';

// The request format for executing http actions from adaptive cards in groups
const ADAPTIVE_CARD_GROUP_ACTION_REQUEST_FORMAT =
    '{0}/{1}/threads/{2}/posts/{3}/adaptiveCard/executeAction?culture={4}';

const executeHttpAction = (
    action: any,
    inputs: Input[],
    cardDetails: CardDetails,
    messageContext: MessageContext,
    isAutoInvokeAction: boolean
): Promise<ExecuteActionResponse> => {
    const actionId = action['@id'] ? action['@id'] : action.id;

    let actionPayload: any = {
        '@type': HTTP_POST_ACTION_TYPE,
        name: action.title,
        target: action.url,
        body: action.body,
        '@id': actionId,
        hideCardOnInvoke: action.hideCardOnInvoke,
        isPrimaryAction: action.isPrimary ? action.isPrimary : false,
    };

    if (action.headers && action.headers.length > 0) {
        actionPayload.headers = action.headers;
    }

    const userConfiguration = getUserConfiguration();
    const inputParameters = [];
    if (inputs) {
        for (let i = 0; i < inputs.length; i++) {
            const input = inputs[i];
            const validationResults = new ValidationResults();
            input.internalValidateProperties(validationResults);
            if (validationResults.failures.length > 0) {
                return Promise.resolve({
                    success: false,
                    displayMessage: validationResults.failures[0].errors[0].message,
                });
            }
            if (!input) {
                continue;
            }

            let inputParameter: any;
            if (input.getJsonTypeName() === 'Input.Date') {
                inputParameter = {
                    id: input.id,
                    value: input.value,
                    usertimezone: userConfiguration.UserOptions.TimeZone,
                };
            } else {
                inputParameter = {
                    id: input.id,
                    value: input.value,
                };
            }

            inputParameters.push(inputParameter);
        }
    }

    const httpAction = {
        type: HttpAction.JsonTypeName,
        url: action.url,
        body: action.body,
        method: action.method,
        id: action.id ? action.id : DEFAULT_ACTION_ID,
        headers: action.headers,
    };

    const userCulture = userConfiguration.SessionSettings.UserCulture;
    let requestPayload;
    const elementDetails: ElementDetails = {
        type: action.getJsonTypeName(),
        id: action.id,
    };
    const telemetryDetails: TelemetryDetails = {
        eventName: getHttpActionEventName(action, isAutoInvokeAction),
        messageContext,
        cardDetails,
        elementDetails,
        actionContext: action.actionContext,
    };
    const clientTelemetry = getTelemetryData(telemetryDetails);

    /**
     * NOTE:
     * getTelemetryData returns these data as a string,
     * so parsing them to return their old shape
     */
    if (clientTelemetry.messageCardContext) {
        try {
            clientTelemetry.messageCardContext = JSON.parse(
                clientTelemetry.messageCardContext as string
            );
        } catch (e) {}
    }

    if (clientTelemetry.messageCardExtensionContext) {
        try {
            clientTelemetry.messageCardExtensionContext = JSON.parse(
                clientTelemetry.messageCardExtensionContext as string
            );
        } catch (e) {}
    }

    if (clientTelemetry.actionContext) {
        try {
            clientTelemetry.actionContext = JSON.parse(clientTelemetry.actionContext as string);
        } catch (e) {}
    }

    if (cardDetails.isAdaptiveCard) {
        requestPayload = {
            httpAction: httpAction,
            inputParameters: inputParameters,
            connectorSenderGuid: cardDetails.connectorSenderGuid,
            SmtpAddressesSerialized: cardDetails.smtpAddressesSerialized,
            adaptiveCardSignature: cardDetails.cardSignature,
            providerAccountUniqueId: cardDetails.providerAccountUniqueId,
            clientTelemetry: clientTelemetry,
            adaptiveCardHash: cardDetails.cardHash,
            senderAddress: messageContext.senderAddress,
        };
    } else {
        // For message card (not adaptive card) initialize the actionPayload with the action's original json payload.
        // Some properties on the original payload may then be replaced
        actionPayload = { ...action.originalJsonPayload, ...actionPayload };
        requestPayload = {
            inputParameters: inputParameters,
            connectorSenderGuid: cardDetails.connectorSenderGuid,
            potentialAction: JSON.stringify(actionPayload),
            SmtpAddressesSerialized: cardDetails.smtpAddressesSerialized,
            messageCardSignature: cardDetails.cardSignature,
            providerAccountUniqueId: cardDetails.providerAccountUniqueId,
            clientTelemetry: clientTelemetry,
            tenantObjectId: userConfiguration.SessionSettings.ExternalDirectoryTenantGuid,
            messageCardHash: cardDetails.cardHash,
            senderAddress: messageContext.senderAddress,
            culture: userCulture,
            uiCulture: userCulture,
        };
    }

    return makePostRequest(
        getTargetUrl(messageContext, userCulture, cardDetails.isAdaptiveCard),
        JSON.stringify(requestPayload),
        'HttpAction',
        null /* correlationVector */,
        getCardCorrelationId(cardDetails),
        isAutoInvokeAction
    );
};

export default executeHttpAction;

const getHttpActionEventName = (action: any, isAutoInvokeAction: boolean) => {
    if (isAutoInvokeAction) {
        return AUTO_INVOKE_ACTION_CLICKED;
    }

    if (action.parent) {
        const constructor = action.parent.constructor;
        return INLINE_ADAPTIVE_CARD_TYPE === constructor.name
            ? ACTION_CARD_POST_ACTION_CLICKED
            : SINGLE_CLICK_POST_ACTION_CLICKED;
    }

    return SINGLE_CLICK_POST_ACTION_CLICKED;
};

const getTargetUrl = (
    messageContext: MessageContext,
    userCulture: string,
    isAdaptiveCard: boolean
): string => {
    const userId =
        messageContext.mailboxInfo.type === 'UserMailbox'
            ? USER_ID
            : messageContext.mailboxInfo.mailboxSmtpAddress;
    const itemId = messageContext.odataItemId;
    const threadId = messageContext.odataConversationId;
    const actionEndpointUrl = getActionEndpointUrlFromStore();

    switch (messageContext.mailboxInfo.type) {
        case 'UserMailbox':
            if (isAdaptiveCard) {
                return format(
                    ADAPTIVE_CARD_ACTION_REQUEST_FORMAT,
                    actionEndpointUrl,
                    userId,
                    itemId,
                    userCulture
                );
            } else {
                return format(
                    MESSAGE_CARD_ACTION_REQUEST_FORMAT,
                    actionEndpointUrl,
                    userId,
                    itemId,
                    userCulture
                );
            }
        case 'GroupMailbox':
        case 'TeamsMailbox':
            if (isAdaptiveCard) {
                return format(
                    ADAPTIVE_CARD_GROUP_ACTION_REQUEST_FORMAT,
                    actionEndpointUrl,
                    userId,
                    threadId,
                    itemId,
                    userCulture
                );
            } else {
                return format(
                    MESSAGE_CARD_GROUP_ACTION_REQUEST_FORMAT,
                    actionEndpointUrl,
                    userId,
                    threadId,
                    itemId,
                    userCulture
                );
            }
        case 'PublicMailbox':
        case 'SharedMailbox':
        case 'ArchiveMailbox':
            return null;
    }
};

const getRequestTimeout = (isAutoInvokeAction: boolean): number => {
    let requestTimeout = isAutoInvokeAction
        ? CLIENT_DEFAULT_AUTO_INVOKE_ACTION_TIMEOUT
        : CLIENT_DEFAULT_HTTP_ACTION_TIMEOUT;
    const clientTimeouts = getClientTimeouts();
    if (isAutoInvokeAction && clientTimeouts?.AutoInvokedActionTimeoutInMsec) {
        requestTimeout = clientTimeouts.AutoInvokedActionTimeoutInMsec;
    } else if (!isAutoInvokeAction && clientTimeouts?.HttpActionTimeoutInMsec) {
        requestTimeout = clientTimeouts.HttpActionTimeoutInMsec;
    }
    return requestTimeout;
};

export const makePostRequest = async (
    url: string,
    requestBody: string,
    requestType: string,
    correlationVector?: string,
    cardCorrelationId?: string,
    isAutoInvokeAction: boolean = false
): Promise<ExecuteActionResponse> => {
    const cv = correlationVector || getCorrelationVector();
    const customDataForErrorLogging: CustomData = {
        correlationVector: cv,
        cardCorrelationId: cardCorrelationId,
        requestType: requestType,
    };
    let token;
    try {
        token = await getClientAuthToken();
        if (!token) {
            trace.warn(
                '[ActionableMessageDebug] makePostRequest: Unable to get the client Auth token possibly AAD token'
            );
            token = await getConnectorsLTIToken();
        }
        if (!token) {
            throw new Error(`Token is empty: ${token}`);
        }
    } catch (error) {
        trace.warn(
            `[ActionableMessageDebug] makePostRequest: Error while getting token, error: ${error}`
        );
        logQoSFailure(
            'ActionableMessageConnectorsPostRequestErrorGettingToken',
            undefined /* duration */,
            undefined /* status */,
            error,
            customDataForErrorLogging,
            { isCore: true }
        );
        return {
            success: false,
            displayMessage: loc(actionGenericError),
        };
    }
    return makePostRequestHelper(
        url,
        token,
        requestBody,
        isAutoInvokeAction,
        cv,
        customDataForErrorLogging
    );
};

const makePostRequestHelper = (
    url: string,
    token: string,
    requestBody: string,
    isAutoInvokeAction: boolean,
    correlationVector: string,
    customDataForErrorLogging: CustomData
): Promise<ExecuteActionResponse> => {
    return new Promise(resolve => {
        const request: XMLHttpRequest = new XMLHttpRequest();
        request.onreadystatechange = function () {
            if (request.readyState === HttpReadyState.Done) {
                resolve(
                    parseResponseText(request.status === HttpStatusCode.OK, request.responseText)
                );
            }
        };

        request.ontimeout = function () {
            resolve({
                success: false,
                displayMessage: loc(actionGenericError),
            });
            logQoSFailure(
                'ActionableMessageConnectorsPostRequestTimeout',
                undefined /* duration */,
                undefined /* status */,
                undefined /* Error */,
                customDataForErrorLogging,
                { isCore: true }
            );
        };

        request.open('POST', url, true);
        request.timeout = getRequestTimeout(isAutoInvokeAction);
        request.setRequestHeader('MS-CV', correlationVector);
        request.setRequestHeader('X-OWA-CANARY', getOwaCanaryCookie());
        request.setRequestHeader('Authorization', `Bearer ${token}`);
        request.send(requestBody);
    });
};

const parseResponseText = (
    httpRequestWasSuccessful: boolean,
    responseText: string
): ExecuteActionResponse => {
    try {
        const response = JSON.parse(responseText);
        const success = httpRequestWasSuccessful
            ? doesResponseStatusIndicateSuccess(response.status)
            : false;
        return {
            success: success,
            status: response.status,
            displayMessage: response.displayMessage
                ? response.displayMessage
                : success
                ? loc(actionGenericSuccess)
                : loc(actionGenericError),
            performedAt: response.performedAt,
            newCardPayload: response.refreshCardSerialized,
            newCardSignature: response.refreshCardSignature,
            removeCard: response.removeCard,
            showOriginalCard: response.showOriginalCard,
            hideCard: response.hideCard,
            authenticationUrl: response.authenticationUrl,
            cardUpdates: response.cardUpdates,
            errors: response.errors,
        };
    } catch (error) {
        return {
            success: false,
            displayMessage: loc(actionGenericError),
        };
    }
};

const doesResponseStatusIndicateSuccess = (responseStatus: string) => {
    return responseStatus && responseStatus.toLowerCase() === 'completed';
};

const getCardCorrelationId = (cardDetails: CardDetails): string => {
    // Wrap this in a try catch since some of the value may be undefined
    try {
        return cardDetails.card.messageCardContext.correlationId;
    } catch (error) {
        return '';
    }
};
